import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { planets } from "../data/planets";
import { portfolioItems } from "../data/portfolio";
import "./ProjectsSpaceChapterPage.css";

export default function ProjectsSpaceChapterPage() {
  const { planetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const scrollerRef = useRef(null);
  const cardRefs = useRef([]);
  const snapTimerRef = useRef(null);
  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startScrollLeft: 0,
    lastX: 0,
    lastTime: 0,
    velocity: 0,
  });
  const momentumRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [revealPhase, setRevealPhase] = useState("enter"); // enter -> expanding -> content -> done

  const planet = useMemo(() => {
    return planets.find((p) => p.id === planetId) || null;
  }, [planetId]);

  const portfolio = useMemo(() => {
    return portfolioItems.find((p) => p.id === planetId) || null;
  }, [planetId]);

  const transition = location.state?.transition || {};
  const accent = planet?.color || transition.color || "#2f7bff";

  // Build card data: only image cards now (no hero/info text)
  const cards = useMemo(() => {
    const items = [];

    // Image cards from portfolio
    const images = portfolio?.images || [];
    images.forEach((url, i) => {
      items.push({
        type: "image",
        url,
        alt: `${portfolio?.title || planetId} — image ${i + 1}`,
        index: i,
      });
    });

    // If no portfolio data, add placeholder cards
    if (!portfolio || images.length === 0) {
      for (let i = 0; i < 4; i++) {
        items.push({ type: "placeholder", index: i });
      }
    }

    return items;
  }, [planetId, portfolio]);

  // Reveal animation timeline
  useEffect(() => {
    let cancelled = false;
    setRevealPhase("enter");
    const t1 = setTimeout(() => {
      if (cancelled) return;
      setRevealPhase("expanding");
    }, 30);
    const t2 = setTimeout(() => {
      if (cancelled) return;
      setRevealPhase("content");
    }, 520);
    const t3 = setTimeout(() => {
      if (cancelled) return;
      setRevealPhase("done");
    }, 1300);
    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  // Lock body scroll
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleClose = useCallback(() => {
    navigate("/projects", {
      state: {
        returnPlanetId: planet?.id || planetId || null,
      },
    });
  }, [navigate, planet?.id, planetId]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleClose]);

  const revealFrom = useMemo(() => {
    const fallbackX = typeof window !== "undefined" ? window.innerWidth / 2 : 0;
    const fallbackY =
      typeof window !== "undefined" ? window.innerHeight / 2 : 0;
    return {
      x: Number.isFinite(transition.x) ? transition.x : fallbackX,
      y: Number.isFinite(transition.y) ? transition.y : fallbackY,
    };
  }, [transition.x, transition.y]);

  // --- Scrolling / Drag / Snap logic ---

  const clearSnapTimer = useCallback(() => {
    if (!snapTimerRef.current) return;
    clearTimeout(snapTimerRef.current);
    snapTimerRef.current = null;
  }, []);

  const cancelMomentum = useCallback(() => {
    if (!momentumRef.current) return;
    cancelAnimationFrame(momentumRef.current);
    momentumRef.current = null;
  }, []);

  const centerCardAt = useCallback((index, behavior = "smooth") => {
    const node = cardRefs.current[index];
    if (!node) return;
    node.scrollIntoView({
      behavior,
      block: "nearest",
      inline: "center",
    });
  }, []);

  const centerNearestCard = useCallback(
    (behavior = "smooth") => {
      const scroller = scrollerRef.current;
      if (!scroller || !cards.length) return;

      const viewportCenter = scroller.scrollLeft + scroller.clientWidth * 0.5;
      let bestIndex = 0;
      let bestDistance = Number.POSITIVE_INFINITY;

      for (let i = 0; i < cards.length; i += 1) {
        const node = cardRefs.current[i];
        if (!node) continue;
        const cardCenter = node.offsetLeft + node.offsetWidth * 0.5;
        const distance = Math.abs(cardCenter - viewportCenter);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = i;
        }
      }

      centerCardAt(bestIndex, behavior);
    },
    [centerCardAt, cards.length],
  );

  const scheduleSnap = useCallback(
    (delayMs = 180) => {
      clearSnapTimer();
      cancelMomentum();
      if (dragRef.current.active) return;
      snapTimerRef.current = window.setTimeout(() => {
        snapTimerRef.current = null;
        if (dragRef.current.active) return;
        centerNearestCard("smooth");
      }, delayMs);
    },
    [centerNearestCard, clearSnapTimer, cancelMomentum],
  );

  // Init scroll position
  useEffect(() => {
    const raf = window.requestAnimationFrame(() => {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      scroller.scrollTo({ left: 0, behavior: "auto" });
    });
    return () => window.cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    return () => {
      clearSnapTimer();
      cancelMomentum();
    };
  }, [clearSnapTimer, cancelMomentum]);

  // Wheel -> horizontal scroll
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const handleWheel = (event) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      cancelMomentum();
      scroller.scrollLeft += event.deltaY;
      scheduleSnap(220);
    };

    scroller.addEventListener("wheel", handleWheel, { passive: false });
    return () => scroller.removeEventListener("wheel", handleWheel);
  }, [scheduleSnap, cancelMomentum]);

  // Resize -> re-center
  useEffect(() => {
    const handleResize = () => {
      if (dragRef.current.active) return;
      centerNearestCard("auto");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [centerNearestCard]);

  // Pointer drag handlers
  const handlePointerDown = useCallback(
    (event) => {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;

      cancelMomentum();
      clearSnapTimer();
      dragRef.current.active = true;
      dragRef.current.pointerId = event.pointerId;
      dragRef.current.startX = event.clientX;
      dragRef.current.startScrollLeft = scroller.scrollLeft;
      dragRef.current.lastX = event.clientX;
      dragRef.current.lastTime = performance.now();
      dragRef.current.velocity = 0;
      setIsDragging(true);
      scroller.setPointerCapture?.(event.pointerId);
    },
    [clearSnapTimer, cancelMomentum],
  );

  const handlePointerMove = useCallback((event) => {
    const scroller = scrollerRef.current;
    if (!scroller || !dragRef.current.active) return;
    const now = performance.now();
    const deltaX = event.clientX - dragRef.current.startX;
    scroller.scrollLeft = dragRef.current.startScrollLeft - deltaX;

    const dt = now - dragRef.current.lastTime;
    if (dt > 0) {
      dragRef.current.velocity = (event.clientX - dragRef.current.lastX) / dt;
    }
    dragRef.current.lastX = event.clientX;
    dragRef.current.lastTime = now;
  }, []);

  const finishDrag = useCallback(() => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    dragRef.current.pointerId = null;
    setIsDragging(false);

    const scroller = scrollerRef.current;
    if (!scroller) return;

    // Momentum
    const velocityX = -dragRef.current.velocity * 16;
    if (Math.abs(velocityX) > 1) {
      let current = velocityX;
      const friction = 0.93;
      const step = () => {
        if (Math.abs(current) < 0.4) {
          momentumRef.current = null;
          scheduleSnap(80);
          return;
        }
        scroller.scrollLeft += current;
        current *= friction;
        momentumRef.current = requestAnimationFrame(step);
      };
      momentumRef.current = requestAnimationFrame(step);
    } else {
      scheduleSnap(120);
    }
  }, [scheduleSnap]);

  const handlePointerUp = useCallback(() => finishDrag(), [finishDrag]);
  const handlePointerCancel = useCallback(() => finishDrag(), [finishDrag]);

  const handleScroll = useCallback(() => {
    if (dragRef.current.active) return;
    scheduleSnap(180);
  }, [scheduleSnap]);

  const chapterStyle = useMemo(
    () => ({
      "--chapter-accent": accent,
      "--reveal-x": `${revealFrom.x}px`,
      "--reveal-y": `${revealFrom.y}px`,
    }),
    [accent, revealFrom.x, revealFrom.y],
  );

  const showContent = revealPhase === "content" || revealPhase === "done";

  // Generate stable random rotations for cards
  const cardRotations = useMemo(() => {
    return cards.map((_, i) => {
      // Deterministic pseudo-random based on index
      const seed = (i * 7 + 3) % 11;
      return (seed - 5) * 0.6; // range roughly -3 to +3 degrees
    });
  }, [cards]);

  return (
    <div
      className={`chapter ${revealPhase !== "done" ? `chapter--${revealPhase}` : ""}`}
      style={chapterStyle}
    >
      {/* Reveal overlay */}
      {revealPhase !== "done" && (
        <div className="chapter__reveal" aria-hidden="true" />
      )}

      {/* Fluid colorful background */}
      <div className="chapter__fluid-bg" aria-hidden="true">
        <div className="chapter__blob chapter__blob--1" />
        <div className="chapter__blob chapter__blob--2" />
        <div className="chapter__blob chapter__blob--3" />
        <div className="chapter__blob chapter__blob--4" />
        <div className="chapter__blob chapter__blob--5" />
      </div>

      {/* Header */}
      <header className={`chapter__header ${showContent ? "is-visible" : ""}`}>
        <Link
          to="/projects"
          state={{ returnPlanetId: planet?.id || planetId || null }}
          className="chapter__logo"
        >
          Planet Archive
        </Link>

        <button type="button" className="chapter__close" onClick={handleClose}>
          <span className="chapter__close-icon">&times;</span>
        </button>
      </header>

      {/* Card scroller */}
      <section
        className={`chapter__scroller-wrap ${showContent ? "is-visible" : ""}`}
      >
        <div
          ref={scrollerRef}
          className={`chapter__scroller ${isDragging ? "is-dragging" : ""}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onScroll={handleScroll}
          role="region"
          aria-label={`${portfolio?.title || planetId} gallery`}
        >
          {cards.map((card, i) => {
            const delay = `${Math.min(i, 8) * 90 + 100}ms`;
            const rotation = cardRotations[i];

            if (card.type === "image") {
              return (
                <article
                  key={`img-${card.index}-${i}`}
                  ref={(node) => {
                    cardRefs.current[i] = node;
                  }}
                  className="chapter__card chapter__card--paper"
                  style={{
                    "--card-delay": delay,
                    "--card-index": i,
                    "--card-rotation": `${rotation}deg`,
                  }}
                >
                  <div className="chapter__paper-frame">
                    <div className="chapter__paper-grain" aria-hidden="true" />
                    <div className="chapter__image-window">
                      <img
                        src={card.url}
                        alt={card.alt}
                        className="chapter__image"
                        loading="lazy"
                        decoding="async"
                        draggable="false"
                      />
                    </div>
                  </div>
                </article>
              );
            }

            // placeholder
            return (
              <article
                key={`placeholder-${card.index}-${i}`}
                ref={(node) => {
                  cardRefs.current[i] = node;
                }}
                className="chapter__card chapter__card--paper chapter__card--empty"
                style={{
                  "--card-delay": delay,
                  "--card-index": i,
                  "--card-rotation": `${rotation}deg`,
                }}
              >
                <div className="chapter__paper-frame">
                  <div className="chapter__paper-grain" aria-hidden="true" />
                  <div className="chapter__image-window chapter__image-window--empty" />
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Bottom bar */}
      <footer className={`chapter__footer ${showContent ? "is-visible" : ""}`}>
        <p className="chapter__hint">DRAG TO EXPLORE</p>
      </footer>
    </div>
  );
}
